/**
 * Reducer for hydrating the store with server-side props that are not dynamic.
 * That is, we will not be modifying them during the lifetime of the client-side
 * app, but are needed for initial loading of the page.
 */

import serverProps from '../utils/serverProps'
import { TRANSFER_BASE_URL, ActionTypes as types } from '../constants'
import {
  ANALYTICS_FLOW_TYPE,
  ENV_TYPE,
  FREEP2P_TYPE,
} from '../../../../lib/constants'
import qs from 'qs'
import { canPhoneSendRequest } from '../../../../lib/utils/capabilities'
import { get } from 'lodash'

function getInitialState(props) {
  // pull out relevant props from server-side props
  const {
    isExternal = false,
    isProduction = true,
    freeP2PType = FREEP2P_TYPE.INAPPLICABLE,
    isEnrolledInBillSplitting: billSplittingPXP = false,
    isV2 = false,
    consentType = '',
    triggerSenderPhoneConfirmation = false,
    businessAccountInfo = {},
    redirectToClassicInfo = {},
    isMassPaySupportedForUserCountry = true,
    ppMeProfile = {},
    userConfig = {},
    locality: { country = '', directionality = 'ltr', locale = '' } = {},
    defaultCurrencyCode = '',
    countryLocalizedName = '',
    sessionEligibility: { paymentTypes = [], supportedPayeeTypes = [] } = {},
    permissions = {},
    permissions: { showAdditionalConversionInfo = false } = {},
    token: csrfToken = '', // csrf token
    env = '',
    rootURL = TRANSFER_BASE_URL,
    recipientData = {},
    features = {},
    groupFeatures = {},
    isSegmentFirstTimeUser = false,
    sendingLimit = '',
    features: {
      displayPARNotes = false,
      displayUserAgreementLink = false,
      displayPayPalPolicyLink = false,
      displayPoolsTermsAndConditions = false,
      isCFPBEnabled: isCFPBEnabledCountry = false,
    } = {},
    links: { userAgreement: { linkUrl: userAgreementLinkUrl = '' } = {} } = {},
    griffinMetadata = {},
    griffinSupportedCountries = [], // list of all griffin-supported countries [{ countryCode: 'US', countryName: 'United States' }]
    cardSpecs = {}, // TODO: move to reducers/card.js
    payRequest: {
      id: requestId = '',
      legId: requestLegId = '',
      amountFormatted: payRequestAmountFormatted = '',
      recipient = '',
      displayName = '',
      suggestedTransactionType = 'UNKNOWN',
      isHardDefault = false,
      thumbnailUrl = '',
      amount: payRequestAmount = '',
      currencyCode: payRequestCurrencyCode = '',
      currencyDecimal: payRequestCurrencyDecimal = '',
      requesterAccountType: payRequestRequesterAccountType = '',
    } = {},
    requester = {},
    isFFEligible = false,
    isAndroid = false,
    isiPhone = false,
    isNativeApp = false,
    showAppBanner = false,
    showAppSlideOnMoreTabBanner = false,
    isUnilateralUser = false,
    hasConfirmedEmail = false,
    isAccountLinkedToXoom = false,
    userHasXoomAccount = false,
    isSenderViewingReveal = false, // relevant to the /reveal flow
    profileInfo: {
      // relevant to the /reveal flow
      entryName: giftProfileEntryName = '',
      fullName: giftProfileFullName = '',
      informalName: giftProfileInformalName = '',
      businessInformalName: giftProfileBusinessInformalName = '',
      thumbnailUrl: giftProfileThumbnailUrl = '',
      receiverUnilateralContact: giftProfileReceiverUnilateralContact = '',
    } = {},
    user: {
      customerId = '',
      entryName: userEntryName = '',
      firstName: userFirstName = '',
      lastName: userLastName = '',
      fullName: userFullName = '',
      informalName: userInformalName = '',
      businessInformalName: userBusinessInformalName = '',
      thumbnailUrl: userThumbnailUrl = '',
      authState: userAuthState = '',
      userAccountType = '',
    } = {},
    settingsError = '',
    isClaimMoneyUrl = false,
    guestSupportedCountries = [], // list of all countries for which we support guest pay request
    isEnrolledInGuestTreatment = false,

    isFBMessenger = false,
    analyticsFlow = 'FNL',
    idpConnect = {},
    externalConfig: {
      isEnrolledInBillSplitting: billSplittingPXPOverride,
    } = {},
    requestType = 'GOODS',
    // COMPLETED , IN PROGRESS and NOT INITIATED
    cipStatus = '',
    sessionID = '',
    isMobile = false,
    // eligibility for various features, e.g. ppme, invoice, masspay, etc.
    featureEligibility = {
      buy: { eligible: true, privileged: true },
      gift: { eligible: true, privileged: true },
      invoice: { eligible: true, privileged: true },
      massPayment: { eligible: true, privileged: true },
      pix: { eligible: true, privileged: true },
      pools: { eligible: true, privileged: true },
      ppme: { eligible: true, privileged: true },
      request: { eligible: true, privileged: true },
      requestGroup: { eligible: true, privileged: true },
      send: { eligible: true, privileged: true },
      xb: { eligible: true, privileged: true },
      xbBiz: { eligible: true, privileged: true },
      split: { eligible: true, privileged: true },
      ppdg: { eligible: true, privileged: true },
      mgm: { eligible: true, privileged: true },
      donation: { eligible: true, privileged: true },
      seller: { eligible: true, privileged: true },
      messages: { eligible: true, privileged: true },
    },
    userSegments = {},
    addContactListRedirectUrl = '',
    decryptedPoolId = '',
    addFunnelRedirectUrl: funnelRedirectUrl = '',
    addFunnelRequestRedirectUrl: funnelRequestRedirectUrl = '',
    addSearchConsentRedirectUrl = '',
    searchTrackingDetails = {},
    pStudioContent = {},
    onboardingPayRequestUrl = '',
    searchDebounceTime = 500,
    appPushModalDelay = 1800,
    appPushModalMaxViews = 3,
    isLoggedInClaim,
    isMgm = false,
    mgmTrafficSource = '',
    flowIntent = '',
    liveHostName = '',
    liveDeveloperHostName = '',
    stageHostName = '',
    cdnHostName = '',
    sellerProfileDetails = {},
    isPSAEnabled = false,
    isLegalEntityUser = '',
    noteFieldRequired = false,
    encryptionFailed = false,
    flowContextData = '',
  } = props

  const isEnrolledInBillSplitting = billSplittingPXPOverride || billSplittingPXP
  // TODO: possibly put hasReceivedData in the loading reducer
  // determine if the server was able to provide data
  const hasReceivedData = Object.keys(props).length > 0

  const isPhoneSendRequestEligible = canPhoneSendRequest(supportedPayeeTypes)

  // TODO: remove when Network ID is live
  const addFunnelRedirectUrl = funnelRedirectUrl || ''
  const addFunnelRequestRedirectUrl = funnelRequestRedirectUrl || ''
  // get if we should showAdditionalConversionInfo from transfer permissions or straight of permissions
  // TODO: we should try to use persmissions straight off the `persmissions` object instead of using `permissions.transfer`
  // const showAdditionalConversionInfo = transferShowAdditionalConversionInfo || permissionsShowAdditionalConversionInfo

  // In pisp,
  // we want to store 3 strings passed from our partners (amex, fidelity, etc)
  // The other redirect flow case is ppcp, which is initiated from mep/dashboard and should redirect there once transaction is complete.
  const { psd2ReturnUrl, psd2CancelUrl } = obtainRedirectFlowData(
    env,
    recipientData
  )

  // set initial state; passed in as default to exported function below
  const initialState = {
    isExternal,
    isProduction,
    freeP2PType,
    isFFEligible,
    isEnrolledInBillSplitting,
    isPhoneSendRequestEligible,
    isAndroid,
    isiPhone,
    isMobile,
    isNativeApp,
    isV2,
    showAppBanner,
    showAppSlideOnMoreTabBanner,
    businessAccountInfo,
    recipientData,
    redirectToClassicInfo,
    isMassPaySupportedForUserCountry,
    ppMeProfile,
    userConfig,
    country,
    defaultCurrencyCode,
    countryLocalizedName,
    directionality,
    locale,
    paymentTypes,
    permissions,
    env,
    csrfToken,
    rootURL,
    features,
    groupFeatures,
    isSegmentFirstTimeUser,
    isUnilateralUser,
    sendingLimit,
    displayPARNotes,
    displayUserAgreementLink,
    displayPayPalPolicyLink,
    displayPoolsTermsAndConditions,
    isCFPBEnabledCountry,
    userAgreementLinkUrl,
    griffinMetadata,
    griffinSupportedCountries,
    cardSpecs,
    requestId,
    requestLegId,
    payRequestAmountFormatted,
    recipient,
    displayName,
    suggestedTransactionType,
    isHardDefault,
    thumbnailUrl,
    payRequestAmount,
    payRequestCurrencyCode,
    payRequestCurrencyDecimal,
    payRequestRequesterAccountType,
    showAdditionalConversionInfo,
    giftProfileEntryName,
    giftProfileFullName,
    giftProfileInformalName,
    giftProfileBusinessInformalName,
    giftProfileThumbnailUrl,
    giftProfileReceiverUnilateralContact,
    isSenderViewingReveal,
    userEntryName,
    userFirstName,
    userLastName,
    userFullName,
    userInformalName,
    userBusinessInformalName,
    userThumbnailUrl,
    userAuthState,
    userAccountType,
    customerId,
    settingsError,
    hasReceivedData,
    isClaimMoneyUrl,
    guestSupportedCountries,
    isEnrolledInGuestTreatment,
    hasConfirmedEmail,
    isAccountLinkedToXoom,
    userHasXoomAccount,

    isFBMessenger,
    analyticsFlow,
    idpConnect,

    requestType,
    psd2: {
      return_url: psd2ReturnUrl,
      cancel_url: psd2CancelUrl,
      token: recipientData.psd2Token,
    },
    cipStatus,
    featureEligibility,
    userSegments,
    requester,
    addContactListRedirectUrl,
    decryptedPoolId,
    addFunnelRedirectUrl,
    addFunnelRequestRedirectUrl,
    addSearchConsentRedirectUrl,
    searchTrackingDetails,
    pStudioContent,
    sessionID,
    onboardingPayRequestUrl,
    consentType,
    searchDebounceTime,
    appPushModalDelay,
    appPushModalMaxViews,
    triggerSenderPhoneConfirmation,
    isLoggedInClaim,
    isMgm,
    mgmTrafficSource,
    flowIntent,
    liveHostName,
    liveDeveloperHostName,
    stageHostName,
    cdnHostName,
    sellerProfileDetails,
    isPSAEnabled,
    isLegalEntityUser,
    noteFieldRequired,
    encryptionFailed,
    flowContextData,
  }
  return initialState
}

export default (state = getInitialState(serverProps), action) => {
  switch (action.type) {
    case types.EXTERNAL.RECEIVED_OPTIONS:
      return {
        ...state,
        env: action.data.env,
        rootURL: action.data.rootURL,
      }
    // TODO: possibly stick hasReceivedData in the loading reducer
    case types.EXTERNAL.RECEIVED_DATA:
      return {
        ...state,
        ...getInitialState({ ...state, ...action.data }),
      }

    case types.TRANSACTION.RECEIVED_SPECS:
      return {
        ...state,
        permissions: action.data.permissions,
      }

    case types.TRANSACTION.UPDATE_GUEST_FF_ELIGIBILITY:
      return {
        ...state,
        isFFEligible: action.data.isFFEligible,
      }

    case types.USER.RECEIVED_GRIFFIN_METADATA:
      return {
        ...state,
        griffinMetadata: action.data.griffinMetadata,
        griffinSupportedCountries: action.data.griffinSupportedCountries,
      }

    case types.ERROR.ENCRYPTION_FAILED:
      return {
        ...state,
        encryptionFailed: action.data.show,
      }

    case types.TRANSACTION.LEAVING_SUCCESS_PAGE:
    case types.TRANSACTION.CANCELLED_TRANSACTION:
      return {
        ...state,
        env: '',
        flowIntent: '',
        isExternal: false,
        analyticsFlow: ANALYTICS_FLOW_TYPE.OTHER,
        encryptionFailed: false,
      }

    case types.EXTERNAL.TOGGLE_PHONE_CONFIRMATION:
      const { otpSuccess } = action.data
      return {
        ...state,
        triggerSenderPhoneConfirmation:
          otpSuccess === 'true' ? false : state.triggerSenderPhoneConfirmation,
      }
    case types.EXTERNAL.REDIRECT_TO_HOMEINFO:
      return {
        ...state,
        env: ENV_TYPE.HOME_INFO,
      }
    case types.TRANSACTION.MGM_INIT:
      const { mgmTrafficSource } = action.data
      return {
        ...state,
        isMgm: true,
        mgmTrafficSource: mgmTrafficSource,
      }
    case types.EXTERNAL.RECEIVED_EXTERNAL_INTEGRATION_PARAMS:
      const { env } = action.data

      switch (env) {
        // Capture the Success status query param for phone confirmation Integration
        // Reset the trigger phone confirmation status values so that its not applicable for next transaction
        case ENV_TYPE.MGM:
          return {
            ...state,
            isMgm: true,
          }
        default:
          return state
      }
    case types.UNILATERAL.CLAIM_ERROR:
      const { settingsError, settingsErrorCode } = action.data

      return {
        ...state,
        ...(settingsError && { settingsError }),
        ...(settingsErrorCode && { settingsErrorCode }),
      }
    default:
      return state
  }
}

/**
 * Obtain pisp flow data that we will use to redirect the user back to partner
 * by using the logic as follows:
 * 1. Try to get return/cancel urls from recipientData collected from getTransactionDetailByToken API
 * 2. Else fallback and get the return/cancel urls from url query params passed by partner
 * 3. Try to obtain state from query param passed from partner
 */
function obtainRedirectFlowData(env, recipientData) {
  let psd2ReturnUrl = ''
  let psd2CancelUrl = ''
  if (env === ENV_TYPE.PISP || env === ENV_TYPE.HAWK_PPCP) {
    const queries = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    })
    const returnUrlFallback = queries.return_url || ''
    const cancelUrlFallback = queries.cancel_url || ''

    psd2ReturnUrl = get(
      recipientData,
      'redirectContext.return_url',
      returnUrlFallback
    )
    psd2CancelUrl = get(
      recipientData,
      'redirectContext.cancel_url',
      cancelUrlFallback
    )
  }

  return { psd2ReturnUrl, psd2CancelUrl }
}
